import React from 'react';
import { 
  Card as MuiCard, 
  CardHeader, 
  CardContent, 
  Box
} from '@mui/material';

/**
 * Card component using Material-UI Card
 * This component provides a consistent interface for cards with headers and actions
 */
const MuiCardComponent = ({
  children,
  variant = 'default',
  padding = 'md',
  header,
  actions,
  className = '',
  onClick,
  sx = {},
  ...props
}) => {
  // Map custom variants to MUI variants
  const getMuiVariant = () => {
    switch (variant) {
      case 'outlined':
        return 'outlined';
      case 'elevated':
        return 'elevation';
      case 'default':
      default:
        return 'elevation';
    }
  };

  // Map custom padding to MUI spacing
  const getPadding = () => {
    switch (padding) {
      case 'sm':
        return { xs: 1, sm: 2 };
      case 'lg':
        return { xs: 3, sm: 4 };
      case 'md':
      default:
        return { xs: 2, sm: 3 };
    }
  };

  // Determine elevation based on variant
  const getElevation = () => {
    if (variant === 'outlined') return 0;
    if (variant === 'elevated') return 3;
    return 1; // default
  };

  const muiVariant = getMuiVariant();
  const paddingValue = getPadding();
  const elevation = getElevation();

  // Handle click events for clickable cards
  const handleClick = onClick ? (e) => onClick(e) : undefined;
  const handleKeyDown = onClick ? (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  } : undefined;

  // Determine if we should render header as CardHeader or custom content
  const renderHeader = () => {
    if (!header) return null;

    if (typeof header === 'string') {
      return (
        <CardHeader 
          title={header}
          action={actions}
          sx={{ 
            pb: 0,
            '& .MuiCardHeader-title': {
              fontSize: '1.125rem',
              fontWeight: 600,
            }
          }}
        />
      );
    }

    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: paddingValue,
        pt: paddingValue,
        pb: 1
      }}>
        <Box>{header}</Box>
        {actions && <Box>{actions}</Box>}
      </Box>
    );
  };

  return (
    <MuiCard
      variant={muiVariant}
      elevation={elevation}
      className={className}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        ...(variant === 'outlined' && { boxShadow: 'none' }),
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: variant === 'outlined' ? 'none' : (theme) => theme.shadows[elevation + 1],
        } : {},
        ...sx
      }}
      {...props}
    >
      {renderHeader()}
      
      <CardContent data-testid="card-content" sx={{ 
        px: paddingValue,
        py: paddingValue,
        '&:last-child': { pb: paddingValue }
      }}>
        {children}
      </CardContent>
      
      {/* Render CardActions if needed but not used in the current implementation */}
    </MuiCard>
  );
};

export default MuiCardComponent;